/**
 * WeaveIn.ai Platform - External Alert Feed & Source Management
 * Shows connected data sources (FEMA, NWS, CDC), allows manual polling,
 * displays ingested alerts with member overlap, and "Create Incident" actions.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  AlertTriangle,
  CloudLightning,
  Activity,
  Users,
  ArrowRight,
  MapPin,
  Clock,
  RefreshCw,
  CheckCircle,
  Globe,
  Zap,
  Database,
} from 'lucide-react';
import { emergencyAPI, ExternalAlert } from '@/services/api/emergency';

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  fema: <AlertTriangle className="w-5 h-5 text-red-500" />,
  nws: <CloudLightning className="w-5 h-5 text-orange-500" />,
  cdc: <Activity className="w-5 h-5 text-purple-500" />,
};

const SOURCE_COLORS: Record<string, string> = {
  fema: 'border-red-200 bg-red-50',
  nws: 'border-orange-200 bg-orange-50',
  cdc: 'border-purple-200 bg-purple-50',
};

interface ExternalAlertFeedProps {
  onIncidentCreated?: (incidentId: number) => void;
  onBack?: () => void;
}

export const ExternalAlertFeed: React.FC<ExternalAlertFeedProps> = ({
  onIncidentCreated,
  onBack,
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'alerts' | 'sources'>('sources');
  const [stateFilter, setStateFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  // Fetch alert sources
  const { data: sourcesData } = useQuery({
    queryKey: ['alert-sources'],
    queryFn: () => emergencyAPI.getAlertSources(),
  });

  // Fetch alerts with filters
  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['external-alerts', sourceFilter, stateFilter, severityFilter],
    queryFn: () => emergencyAPI.getExternalAlerts({
      ...(sourceFilter ? { source: sourceFilter } : {}),
      ...(stateFilter ? { state: stateFilter } : {}),
      ...(severityFilter ? { severity: severityFilter } : {}),
    }),
    refetchInterval: 60000,
  });

  // Poll mutation — updates alert list directly from poll response
  const pollMutation = useMutation({
    mutationFn: (source?: string) => emergencyAPI.pollAlerts(source, stateFilter || undefined),
    onSuccess: (data) => {
      // Poll response includes refreshed alerts — update cache directly
      if (data.alerts) {
        queryClient.setQueryData(
          ['external-alerts', sourceFilter, stateFilter, severityFilter],
          { alerts: data.alerts }
        );
      }
      queryClient.invalidateQueries({ queryKey: ['external-alerts'] });
      // Auto-switch to alerts tab to show results
      if (data.total_new > 0) {
        setActiveTab('alerts');
      }
    },
  });

  // Create incident mutation
  const createIncidentMutation = useMutation({
    mutationFn: ({ alertId, name }: { alertId: number; name?: string }) =>
      emergencyAPI.createIncidentFromAlert(alertId, name),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['external-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['emergency-incidents'] });
      if (onIncidentCreated && data.incident_id) {
        onIncidentCreated(data.incident_id);
      }
    },
  });

  const sources = sourcesData?.sources || [];
  const alerts = alertsData?.alerts || [];

  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC','PR','VI','GU','AS',
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="text-ink-400 hover:text-ink-500">&larr;</button>
            )}
          </div>
          <button
            onClick={() => pollMutation.mutate('all')}
            disabled={pollMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${pollMutation.isPending ? 'animate-spin' : ''}`} />
            {pollMutation.isPending ? 'Polling...' : 'Poll All Sources Now'}
          </button>
        </div>

        {/* Poll results banner */}
        {pollMutation.data && (
          <div className="mt-3 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm">
            <span className="font-medium text-green-700">
              Poll complete: {pollMutation.data.total_new} new alert(s) ingested
            </span>
            <span className="text-green-600 ml-2">
              {Object.entries(pollMutation.data.poll_results).map(([src, count]) =>
                `${src.toUpperCase()}: ${typeof count === 'number' ? count : 'error'}`
              ).join(' | ')}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-3">
          <button
            onClick={() => setActiveTab('sources')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              activeTab === 'sources' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Database className="w-3 h-3 inline mr-1" />
            Data Sources ({sources.length})
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              activeTab === 'alerts' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Bell className="w-3 h-3 inline mr-1" />
            Ingested Alerts ({alerts.length})
          </button>
        </div>
      </div>

      {/* Sources Tab */}
      {activeTab === 'sources' && (
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            These external data sources are monitored for emergency events that may affect your members.
            Alerts are automatically matched against member geography to estimate impact.
          </p>

          {sources.map(source => (
            <div key={source.id} className="border rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {SOURCE_ICONS[source.id] || <Globe className="w-5 h-5 text-gray-400" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{source.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        source.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {source.status === 'active' ? 'Connected' : 'Planned'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{source.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {source.api_url}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {source.poll_frequency}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="font-medium">Data includes:</span> {source.data_includes}
                    </div>
                  </div>
                </div>
                {source.status === 'active' && (
                  <button
                    onClick={() => pollMutation.mutate(source.id)}
                    disabled={pollMutation.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Zap className="w-3 h-3" />
                    Poll Now
                  </button>
                )}
              </div>
            </div>
          ))}

          {sources.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">
              Loading data sources...
            </div>
          )}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="p-6 space-y-3">
          {/* Filters */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
            <span className="text-xs font-medium text-gray-600">Filter:</span>
            <select
              value={stateFilter}
              onChange={e => setStateFilter(e.target.value)}
              className="px-2 py-1 border rounded text-xs bg-white"
            >
              <option value="">All States</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              className="px-2 py-1 border rounded text-xs bg-white"
            >
              <option value="">All Sources</option>
              {sources.filter(s => s.status === 'active').map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value)}
              className="px-2 py-1 border rounded text-xs bg-white"
            >
              <option value="">All Severities</option>
              <option value="emergency">Emergency</option>
              <option value="warning">Warning</option>
              <option value="watch">Watch</option>
              <option value="advisory">Advisory</option>
            </select>
            {(stateFilter || sourceFilter || severityFilter) && (
              <button
                onClick={() => { setStateFilter(''); setSourceFilter(''); setSeverityFilter(''); }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
            <span className="ml-auto text-xs text-gray-400">{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</span>
          </div>

          {alertsLoading && (
            <div className="text-center py-12 text-gray-400 text-sm">Loading alerts...</div>
          )}

          {!alertsLoading && alerts.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-gray-700 mb-1">No Alerts Ingested Yet</h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto mb-4">
                Click "Poll All Sources Now" above to fetch the latest emergency declarations
                and weather alerts from FEMA and the National Weather Service.
              </p>
              <button
                onClick={() => { pollMutation.mutate('all'); }}
                disabled={pollMutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${pollMutation.isPending ? 'animate-spin' : ''}`} />
                {pollMutation.isPending ? 'Polling Sources...' : 'Fetch Alerts Now'}
              </button>
            </div>
          )}

          {alerts.map(alert => {
            const srcColor = SOURCE_COLORS[alert.source] || 'border-gray-200 bg-gray-50';
            const srcIcon = SOURCE_ICONS[alert.source] || <Globe className="w-4 h-4" />;
            const geo = alert.affected_geography || {};
            const geoText = [
              ...(geo.states || []),
              ...(geo.counties || []).slice(0, 3),
            ].join(', ');

            return (
              <div key={alert.id} className={`border rounded-lg p-4 ${srcColor}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {srcIcon}
                      <span className="text-xs font-bold text-gray-700">{alert.source.toUpperCase()}</span>
                      <span className="text-xs text-gray-500">{alert.alert_type.replace(/_/g, ' ')}</span>
                      {alert.severity && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          alert.severity === 'emergency' ? 'bg-red-200 text-red-800' :
                          alert.severity === 'warning' ? 'bg-orange-200 text-orange-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {alert.severity}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">{alert.title}</h3>
                    {alert.description && (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{alert.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className={`flex items-center gap-1 font-medium ${
                      alert.matched_member_count > 0 ? 'text-blue-700' : 'text-gray-400'
                    }`}>
                      <Users className="w-3 h-3" />
                      {alert.matched_member_count} member{alert.matched_member_count !== 1 ? 's' : ''} in area
                    </span>
                    {geoText && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {geoText}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {alert.ingested_at ? new Date(alert.ingested_at).toLocaleString() : ''}
                    </span>
                  </div>

                  <button
                    onClick={() => createIncidentMutation.mutate({ alertId: alert.id, name: alert.title })}
                    disabled={createIncidentMutation.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    <ArrowRight className="w-3 h-3" />
                    Create Incident
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExternalAlertFeed;
